/**
 * m3 echo Frontend - Project Detail Page (Refactored)
 *
 * Detailansicht für ein Projekt mit allen Jobs, Statistiken und Visualisierungen.
 * Refactored to use React Query hooks and Zustand stores.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Breadcrumbs,
  Link,
  Alert,
  LinearProgress,
  Tooltip as MuiTooltip,
  Tabs,
  Tab,
  Snackbar,
  Checkbox,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Fade,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReplayIcon from '@mui/icons-material/Replay';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import DownloadIcon from '@mui/icons-material/Download';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useParams, useNavigate } from 'react-router-dom';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import JobDetailDialog from '../components/JobDetailDialog';
import PromptComparisonDialog from '../components/PromptComparisonDialog';
import OptimizationTab from '../components/OptimizationTab';
import type { OptimizationTabHandle } from '../components/OptimizationTab';
import HomepageUrlDialog from '../components/HomepageUrlDialog';
import AutopilotManagementCard from '../components/AutopilotManagementCard';
import HeatmapChart from '../components/HeatmapChart';
import CitationSharePanel from '../components/CitationSharePanel';
import { translateStatus } from '../utils/statusTranslations';
import { useReducedMotion } from '../hooks/useReducedMotion';
import { analyzePromptQuality } from '../utils/promptQuality';
import { useCountUp } from '../hooks/useCountUp';
import ProjectDetailSkeleton from '../components/skeletons/ProjectDetailSkeleton';
import LiveDot from '../components/LiveDot';

// New hooks and stores
import { useProject, usePromptBreakdown, useRetestProject, useExportProjectCSV } from '../hooks/useProjects';
import { useJobsProgress } from '../hooks/useJobs';
import { useCurrentBenchmarkModels } from '../hooks/useModelCatalog';
import { useRetestOptimization, useCreateOptimizationTest, useOptimizationTests, useOptimizationQuota } from '../hooks/useOptimization';
import { useUIStore } from '../stores/useUIStore';
import { useTenant, useIsAdmin, useCanEdit } from '../stores/useAuthStore';
import { useQuery } from '@tanstack/react-query';
import { getAutopilotQuota } from '../services/autopilots';
import { billingApi } from '../services/billing';

/**
 * Orders runs by creation time, ties broken by id, so two runs started in the
 * same second always appear in the same order (the backend sorts the same way).
 */
const compareJobsByCreation = (
  a: { created_at: string; id: number },
  b: { created_at: string; id: number },
): number => new Date(a.created_at).getTime() - new Date(b.created_at).getTime() || a.id - b.id;

const ProjectDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const projectId = parseInt(id!);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const reduceMotion = useReducedMotion();

  // Use React Query hooks for data fetching
  const { data: project, isLoading, error: projectError } = useProject(projectId);
  const { data: promptBreakdown } = usePromptBreakdown(projectId);
  // Aufstellung des Projekt-Mandanten (Admins sehen auch fremde Projekte)
  const { data: benchmarkModels = [] } = useCurrentBenchmarkModels(project?.tenant_id, {
    enabled: !!project,
  });

  // Use Zustand stores for UI state
  const { openJobDetailDialog } = useUIStore();
  const tenant = useTenant();
  const isAdmin = useIsAdmin();
  const canEdit = useCanEdit();

  // Use mutations for actions
  const retestMutation = useRetestProject();
  const exportMutation = useExportProjectCSV();
  const retestOptimizationMutation = useRetestOptimization();
  const createOptimizationMutation = useCreateOptimizationTest();
  const { data: optimizationTests } = useOptimizationTests(projectId);
  const { quotaExhausted } = useOptimizationQuota();

  // Derive cooldown state from optimization tests (mirrors backend logic:
  // any pending/running test OR any completed test within 24h window)
  const benchmarkCooldown = useMemo(() => {
    if (!optimizationTests || optimizationTests.length === 0) {
      return { onCooldown: false, inProgress: false, cooldownUntil: null as Date | null };
    }

    // Check for any in-progress test
    const hasInProgress = optimizationTests.some(
      t => t.status === 'pending' || t.status === 'running'
    );
    if (hasInProgress) {
      return { onCooldown: true, inProgress: true, cooldownUntil: null };
    }

    // Check for any completed test within 24h window
    const now = new Date();
    for (const t of optimizationTests) {
      if (t.status === 'completed' && t.completed_at) {
        const completedAt = new Date(t.completed_at.endsWith('Z') ? t.completed_at : t.completed_at + 'Z');
        const cooldownUntil = new Date(completedAt.getTime() + 24 * 60 * 60 * 1000);
        if (cooldownUntil > now) {
          return { onCooldown: true, inProgress: false, cooldownUntil };
        }
      }
    }

    return { onCooldown: false, inProgress: false, cooldownUntil: null };
  }, [optimizationTests]);

  // Subscribe to WebSocket updates for all jobs in this project
  const jobIds = useMemo(() => project?.jobs?.map(job => job.id) || [], [project?.jobs]);
  const jobProgress = useJobsProgress(jobIds);

  // State for Prompt Comparison Dialog
  const [promptComparisonOpen, setPromptComparisonOpen] = useState(false);
  const [selectedPromptId, setSelectedPromptId] = useState<number | null>(null);
  const [selectedPromptIndex, setSelectedPromptIndex] = useState<number | null>(null);

  // State for tabs (V3.0)
  const [activeTab, setActiveTab] = useState(0);
  const [homepageDialogOpen, setHomepageDialogOpen] = useState(false);

  // State for autopilot dialog (V4.1.0)
  const [autopilotDialogOpen, setAutopilotDialogOpen] = useState(false);

  // Ref for OptimizationTab to call retest from parent
  const optimizationTabRef = useRef<OptimizationTabHandle>(null);

  // State for 7T average toggle in charts
  const [show7TAvg, setShow7TAvg] = useState(false);
  const [show7TAvgBreakdown, setShow7TAvgBreakdown] = useState(false);

  // Fetch autopilot quota - pass tenant_id from project for admin users
  const { data: autopilotQuota } = useQuery({
    queryKey: ['autopilotQuota', project?.tenant_id],
    queryFn: () => getAutopilotQuota(project?.tenant_id),
    enabled: !!project,
  });

  // Check billing module for Optimierung tab and Manual LLM Score gate
  const [hasOptimierungModule, setHasOptimierungModule] = useState(true); // default true to avoid flash
  // Default false: hide the manual button until billing confirms — accidentally
  // exposing it is a cost risk, not a UX issue.
  const [allowManualLlmScore, setAllowManualLlmScore] = useState(false);
  useEffect(() => {
    billingApi.getMyConfig().then((res) => {
      setHasOptimierungModule(!!res.data.active_features['show_benchmark_tab']);
      setAllowManualLlmScore(!!res.data.active_features['allow_manual_llm_score']);
    }).catch(() => {
      setHasOptimierungModule(true); // backward compat
      setAllowManualLlmScore(false);
    });
  }, []);

  // Compute available tabs based on tenant settings and billing module
  const showBenchmarkTab = tenant?.show_benchmark_tab !== false && hasOptimierungModule;
  const tabs = useMemo(() => {
    const tabList = [
      { label: 'Analyse', key: 'analyse' },
      ...(showBenchmarkTab ? [{ label: 'Optimierung', key: 'benchmark' }] : []),
      { label: 'Autopilot', key: 'autopilot' },
    ];
    return tabList;
  }, [showBenchmarkTab]);

  // Map tab keys to indices for content rendering
  const tabIndexMap = useMemo(() => {
    const map: Record<string, number> = {};
    tabs.forEach((tab, index) => {
      map[tab.key] = index;
    });
    return map;
  }, [tabs]);

  // State for toast notification
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  /**
   * Shows a toast notification
   */
  const showToast = (message: string) => {
    setToastMessage(message);
    setToastOpen(true);
  };

  /**
   * Closes the toast notification
   */
  const handleToastClose = () => {
    setToastOpen(false);
  };

  /**
   * Handles retest action
   */
  const handleRetest = () => {
    retestMutation.mutate(projectId);
    showToast('LLM Score gestartet');
  };

  /**
   * Handles benchmark retest action (with quota/cooldown error handling)
   */
  const handleBenchmarkRetest = async () => {
    if (optimizationTabRef.current) {
      try {
        await optimizationTabRef.current.triggerRetest();
        showToast('Job gestartet');
      } catch (err: any) {
        const status = err?.response?.status;
        if (status === 429) {
          const detail = err?.response?.data?.detail;
          showToast(typeof detail === 'object' ? detail.error : (detail || 'Kontingent/Cooldown aktiv'));
        } else {
          showToast('Fehler beim Starten des Tests');
        }
      }
    }
  };

  /**
   * Opens job detail dialog
   */
  const handleViewJobDetails = (jobId: number) => {
    openJobDetailDialog(jobId);
  };

  /**
   * Scrolls to a job row in the Test History table and opens its detail dialog
   */
  const handleHeatmapCellClick = (jobId: number) => {
    const el = document.getElementById(`job-${jobId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Brief highlight effect
      el.style.transition = 'background-color 0.3s';
      el.style.backgroundColor = 'rgba(103, 139, 163, 0.15)';
      setTimeout(() => {
        el.style.backgroundColor = '';
      }, 1500);
    }
    // Open detail dialog after a short delay so the scroll is visible
    setTimeout(() => {
      openJobDetailDialog(jobId);
    }, 500);
  };

  /**
   * Handles CSV export
   */
  const handleExportCSV = () => {
    if (project) {
      exportMutation.mutate({
        projectId,
        projectName: project.name,
      });
    }
  };

  /**
   * Handles legend click to open prompt comparison dialog
   */
  const handleLegendClick = (data: any) => {
    if (!promptBreakdown || !promptBreakdown.prompts) return;

    const dataKey = data.dataKey;

    // Extract prompt ID from dataKey (format: "prompt_123")
    const match = dataKey.match(/prompt_(\d+)/);
    if (match) {
      const promptId = parseInt(match[1]);
      const promptIndex = promptBreakdown.prompts.findIndex(p => p.id === promptId);

      setSelectedPromptId(promptId);
      setSelectedPromptIndex(promptIndex);
      setPromptComparisonOpen(true);
    }
  };

  /**
   * Formats date for display
   */
  const formatDate = (dateString: string, short = false): string => {
    const utcDateString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
    const date = new Date(utcDateString);
    if (short) {
      return date.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
      });
    }
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  /**
   * Prepares data for trend chart
   */
  const prepareTrendData = () => {
    if (!project || !project.jobs) return [];

    const sortedJobs = [...project.jobs].sort(
      (a, b) => compareJobsByCreation(a, b)
    );

    return sortedJobs.map((job, index) => ({
      name: `#${index + 1}`,
      date: formatDate(job.created_at),
      score: job.average_score,
      jobId: job.id,
    }));
  };

  /**
   * Calculates average of an array of numbers; null when there is none
   * (a real 0 counts, a missing value does not).
   */
  const calculateAverage = (values: number[]): number | null => {
    if (values.length === 0) return null;
    return values.reduce((sum, val) => sum + val, 0) / values.length;
  };

  /**
   * Prepares 7-value average data for trend chart. The groups stay formed by
   * runs; only runs with a score count, and the tooltip names how many.
   */
  const prepare7TAvgData = () => {
    const data = prepareTrendData();
    if (data.length < 7) return data;

    const avgData: Array<{ name: string; score: number | null; validCount: number }> = [];
    for (let i = 0; i <= data.length - 7; i += 7) {
      const chunk = data.slice(i, i + 7);
      const scores = chunk.map(d => d.score).filter((s): s is number => s !== null && s !== undefined);
      const avg = calculateAverage(scores);
      const startIndex = i + 1;
      const endIndex = Math.min(i + 7, data.length);
      avgData.push({
        name: `#${startIndex}-${endIndex}`,
        score: avg !== null ? Math.round(avg * 10) / 10 : null,
        validCount: scores.length,
      });
    }
    return avgData;
  };

  /**
   * Prepares data for prompt breakdown chart
   */
  const preparePromptBreakdownData = () => {
    if (!promptBreakdown || !promptBreakdown.data) return [];

    return promptBreakdown.data.map((item) => {
      const result: any = {
        name: item.test_name.replace('Test ', '#'),
        job_id: item.job_id,
      };

      // Add prompt scores
      Object.keys(item).forEach(key => {
        if (key.startsWith('prompt_') && !key.endsWith('_label')) {
          result[key] = item[key];
        }
      });

      return result;
    });
  };

  /**
   * Prepares 7-value average data for prompt breakdown chart
   */
  const prepare7TAvgBreakdownData = () => {
    const data = preparePromptBreakdownData();
    if (data.length < 7) return data;

    const avgData: any[] = [];
    for (let i = 0; i <= data.length - 7; i += 7) {
      const chunk = data.slice(i, i + 7);
      const startIndex = i + 1;
      const endIndex = Math.min(i + 7, data.length);
      const result: any = { name: `#${startIndex}-${endIndex}` };

      if (promptBreakdown && promptBreakdown.prompts) {
        for (const prompt of promptBreakdown.prompts) {
          const key = `prompt_${prompt.id}`;
          const scores = chunk.map(d => d[key]).filter((s: any) => s !== null && s !== undefined);
          result[`${key}_n`] = scores.length;
          if (scores.length > 0) {
            result[key] = Math.round((scores.reduce((sum: number, val: number) => sum + val, 0) / scores.length) * 10) / 10;
          }
        }
      }

      avgData.push(result);
    }
    return avgData;
  };

  /**
   * Tooltip value of a 7-run group: the mean plus how many runs in the group
   * had a valid value (runs without one do not count towards the mean).
   */
  const formatSevenRunValue = (
    value: number | string | Array<number | string> | null | undefined,
    name: number | string,
    item: { dataKey?: unknown; payload?: Record<string, unknown> },
  ): [string | number | Array<number | string>, number | string] => {
    const key = item?.dataKey === 'score' ? 'validCount' : `${String(item?.dataKey)}_n`;
    const count = item?.payload?.[key];
    const shown = value === null || value === undefined ? '-' : value;
    return [typeof count === 'number' ? `${shown} (${count} von 7 gültig)` : shown, name];
  };

  /**
   * Gets color for prompt bars in chart
   */
  const getPromptColors = () => {
    const colors = ['#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#8dd1e1'];
    return colors;
  };

  /**
   * Gets status color
   */
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'running':
        return 'primary';
      case 'failed':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  /**
   * Gets trend icon. Without a score on both sides there is no comparison,
   * so no trend is shown (any difference > 0 counts as a change here).
   */
  const getTrendIcon = (currentScore: number | null | undefined, previousScore: number | null | undefined) => {
    if (currentScore == null || previousScore == null) {
      return <Typography component="span" color="text.secondary">-</Typography>;
    }
    if (currentScore > previousScore) return <TrendingUpIcon sx={{ color: 'success.main' }} />;
    if (currentScore < previousScore) return <TrendingDownIcon sx={{ color: 'error.main' }} />;
    return <TrendingFlatIcon sx={{ color: 'grey.500' }} />;
  };

  /**
   * Score of a run for the job lists. A finished run without a valid result
   * has no score: it is "nicht auswertbar", not a completed run scoring 0.
   */
  const formatJobScore = (job: { status: string; average_score: number | null }) => {
    if (job.average_score !== null && job.average_score !== undefined) {
      return job.average_score.toFixed(2);
    }
    return job.status === 'completed' || job.status === 'failed' ? 'nicht auswertbar' : '-';
  };

  // Calculate statistics from jobs
  const statistics = useMemo(() => {
    if (!project || !project.jobs) {
      return {
        averageScore: null,
        totalJobs: 0,
        trend: null,
        champions: [],
      };
    }

    const totalJobs = project.jobs.length;

    // Calculate average score from last 10 jobs with scores
    const jobsWithScores = project.jobs
      .filter(job => job.average_score !== null && job.average_score !== undefined)
      .sort((a, b) => compareJobsByCreation(b, a))
      .slice(0, 10);
    const averageScore = jobsWithScores.length > 0
      ? jobsWithScores.reduce((sum, job) => sum + (job.average_score || 0), 0) / jobsWithScores.length
      : null;

    // Calculate trend by comparing latest job to second-to-last job
    let trend: 'up' | 'down' | 'stable' | null = null;
    if (project.jobs.length >= 2) {
      const sortedJobs = [...project.jobs].sort(
        (a, b) => compareJobsByCreation(b, a)
      );
      const latestJob = sortedJobs[0];
      const previousJob = sortedJobs[1];

      if (latestJob.average_score !== null && previousJob.average_score !== null) {
        if (latestJob.average_score > previousJob.average_score) {
          trend = 'up';
        } else if (latestJob.average_score < previousJob.average_score) {
          trend = 'down';
        } else {
          trend = 'stable';
        }
      }
    }

    // Get champions from backend statistics if available
    const champions = project.statistics?.champions || [];

    return {
      averageScore,
      totalJobs,
      trend,
      champions,
    };
  }, [project]);

  // Animated count-up for KPI values
  const animatedTotalJobs = useCountUp(statistics.totalJobs, 400, 0);
  const animatedAvgScore = useCountUp(statistics.averageScore, 400);

  // Memoize chart data
  const trendData = useMemo(() => prepareTrendData(), [project]);
  const trendData7TAvg = useMemo(() => prepare7TAvgData(), [project]);
  const breakdownData = useMemo(() => preparePromptBreakdownData(), [promptBreakdown]);
  const breakdownData7TAvg = useMemo(() => prepare7TAvgBreakdownData(), [promptBreakdown]);

  // Loading state
  if (isLoading) {
    return <ProjectDetailSkeleton />;
  }

  // Error state
  if (projectError || !project) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Projekt konnte nicht geladen werden
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/')}
          sx={{ mt: 2 }}
        >
          Zurück zur Übersicht
        </Button>
      </Box>
    );
  }

  /**
   * Handles opening homepage URL dialog
   */
  const handleConfigureHomepage = () => {
    setHomepageDialogOpen(true);
  };

  /**
   * Handles successful homepage URL save - automatically starts first optimization test if none exist
   */
  const handleHomepageUrlSuccess = async (newUrl: string) => {
    // If no optimization tests exist yet, automatically start the first one
    // Backend enforces quota + cooldown, so just attempt it
    if (!optimizationTests || optimizationTests.length === 0) {
      try {
        await createOptimizationMutation.mutateAsync({
          projectId,
          data: { homepage_url: newUrl },
        });
        showToast('Optimierungs-Test gestartet');
      } catch (error: any) {
        const status = error?.response?.status;
        if (status === 429) {
          const detail = error?.response?.data?.detail;
          showToast(detail?.error || 'Kontingent aufgebraucht oder Cooldown aktiv');
        } else {
          console.error('Failed to create optimization test:', error);
        }
      }
    }
  };

  /**
   * Handles tab change
   */
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Box p={{ xs: 1, sm: 3 }}>
      {/* Header: Breadcrumbs with Back Button and Action Buttons */}
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} gap={1} mb={3}>
        {/* Left: Back button and Breadcrumbs */}
        <Box display="flex" alignItems="center" gap={1} minWidth={0}>
          <IconButton
            size="small"
            onClick={() => navigate('/')}
            sx={{ ml: -1 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Breadcrumbs
            sx={{
              '& .MuiBreadcrumbs-separator': {
                fontSize: { xs: '1rem', sm: '1.5rem' },
              },
            }}
          >
            <Link
              underline="hover"
              color="inherit"
              href="/"
              onClick={(e) => {
                e.preventDefault();
                navigate('/');
              }}
              sx={{
                fontSize: { xs: '1rem', sm: '1.5rem' },
                fontWeight: 700,
              }}
            >
              Dashboard
            </Link>
            <Typography
              color="text.primary"
              noWrap
              sx={{
                fontSize: { xs: '1rem', sm: '1.5rem' },
                fontWeight: 700,
                maxWidth: { xs: 150, sm: 'none' },
              }}
            >
              {project.name}
            </Typography>
          </Breadcrumbs>
        </Box>

        {/* Right: Action Buttons */}
        <Box display="flex" gap={1} flexWrap="wrap">
          {activeTab === tabIndexMap['autopilot'] ? (
            canEdit && (
              /*
                Not disabled at `available === 0`. That number counts
                `consumed`, which manual tests spend and which never enters the
                steady-state check, so a tenant that spent its month manually can
                still legitimately start a monthly autopilot whose day has
                already passed - it costs nothing until next month. Locking the
                button here would have hidden the dialog that says so. The
                exhausted month is stated instead, and the dialog's preview
                decides; the server decides again on save.
              */
              <MuiTooltip
                title={
                  autopilotQuota && autopilotQuota.available === 0
                    ? 'Das Kontingent für diesen Monat ist ausgeschöpft. Der Dialog zeigt, ob eine Konfiguration trotzdem passt.'
                    : ''
                }
              >
                <Button
                  variant="contained"
                  size={isMobile ? 'small' : 'medium'}
                  startIcon={<AddIcon />}
                  onClick={() => setAutopilotDialogOpen(true)}
                >
                  Autopilot erstellen
                </Button>
              </MuiTooltip>
            )
          ) : (
            <>
              <Button
                variant="outlined"
                size={isMobile ? 'small' : 'medium'}
                startIcon={<DownloadIcon />}
                onClick={handleExportCSV}
                disabled={exportMutation.isPending}
              >
                {exportMutation.isPending ? 'Exportiere...' : 'CSV Export'}
              </Button>
              {canEdit && (isAdmin || !(activeTab === tabIndexMap['analyse'] && !allowManualLlmScore)) && (
                <Button
                  variant="contained"
                  size={isMobile ? 'small' : 'medium'}
                  startIcon={<ReplayIcon />}
                  onClick={activeTab === tabIndexMap['analyse'] ? handleRetest : handleBenchmarkRetest}
                  disabled={
                    activeTab === tabIndexMap['analyse']
                      ? retestMutation.isPending || !project?.jobs?.length
                      : retestOptimizationMutation.isPending || !project?.homepage_url || (!isAdmin && (quotaExhausted || benchmarkCooldown.onCooldown))
                  }
                >
                  {activeTab === tabIndexMap['analyse']
                    ? (retestMutation.isPending ? 'Erstelle...' : 'LLM Score')
                    : (retestOptimizationMutation.isPending ? 'Erstelle...' : 'Optimierung')}
                </Button>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* Description (if exists) */}
      {project.description && (
        <Box mb={3}>
          <Typography variant="body1" color="text.secondary">
            {project.description}
          </Typography>
        </Box>
      )}

      {/* Tabs Navigation (V3.0) */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} variant={isMobile ? 'fullWidth' : 'standard'}>
          {tabs.map((tab) => (
            <Tab key={tab.key} label={tab.label} sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }} />
          ))}
        </Tabs>
      </Box>

      {/* Tab Content: Analyse Tab */}
      {activeTab === tabIndexMap['analyse'] && (
        <Fade in timeout={reduceMotion ? 0 : theme.motion.duration.base}><Box>
          {/* Failed job warning */}
          {project.jobs?.[0]?.status === 'failed' && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Der letzte Test ist fehlgeschlagen.
            </Alert>
          )}
          {/* Statistics Cards */}
          <Grid container spacing={{ xs: 1.5, sm: 3 }} mb={3}>
        <Grid item xs={6} sm={6} md={2.4}>
          <Card sx={{ height: '100%', '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 } }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <MuiTooltip title="Der Suchbegriff, für den die Tests durchgeführt wurden" arrow>
                <Typography color="text.secondary" gutterBottom sx={{ cursor: 'help', fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                  Suchbegriff
                </Typography>
              </MuiTooltip>
              <Typography sx={{ fontSize: { xs: '1.1rem', sm: '1.5rem' }, fontWeight: 700, wordBreak: 'break-word' }}>
                {project.search_term}
              </Typography>
              {(project.search_term_synonym1 || project.search_term_synonym2) && (
                <Typography color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' }, fontWeight: 400, mt: 0.5 }}>
                  ({[project.search_term_synonym1, project.search_term_synonym2].filter(Boolean).join(', ')})
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={2.4}>
          <Card sx={{ height: '100%', '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 } }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <MuiTooltip title="Die Gesamtanzahl der durchgeführten Tests für dieses Projekt" arrow>
                <Typography color="text.secondary" gutterBottom sx={{ cursor: 'help', fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                  Anzahl Tests
                </Typography>
              </MuiTooltip>
              <Typography sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' }, fontWeight: 700 }}>
                {animatedTotalJobs}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={2.4}>
          <Card sx={{ height: '100%', '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 } }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <MuiTooltip title="Die Entwicklung der Punktzahl im Vergleich zum vorherigen Test" arrow>
                <Typography color="text.secondary" gutterBottom sx={{ cursor: 'help', fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                  Trend
                </Typography>
              </MuiTooltip>
              <Box display="flex" alignItems="center" justifyContent="flex-start" minHeight={{ xs: 36, sm: 48 }}>
                {statistics.trend === 'up' && <TrendingUpIcon sx={{ fontSize: { xs: 36, sm: 48 }, color: 'success.main' }} />}
                {statistics.trend === 'down' && <TrendingDownIcon sx={{ fontSize: { xs: 36, sm: 48 }, color: 'error.main' }} />}
                {statistics.trend === 'stable' && <TrendingFlatIcon sx={{ fontSize: { xs: 36, sm: 48 }, color: 'grey.500' }} />}
                {statistics.trend === null && <Typography sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' }, fontWeight: 700 }}>-</Typography>}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={2.4}>
          <Card sx={{ height: '100%', '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 } }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <MuiTooltip title={`Durchschnittlicher Score der letzten ${Math.min(10, project?.jobs?.filter(j => j.average_score != null).length || 0)} bewerteten Tests (nicht auswertbare Läufe zählen nicht)`} arrow>
                <Typography color="text.secondary" gutterBottom sx={{ cursor: 'help', fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                  ⌀ Punktzahl
                </Typography>
              </MuiTooltip>
              <Typography sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' }, fontWeight: 700 }}>
                {animatedAvgScore}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={12} md={2.4}>
          <Card sx={{ height: '100%', '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 } }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <MuiTooltip title="Die am häufigsten zuerst genannte Firma in den letzten 20 Tests. Marken und Produkte sind technisch eingeschlossen; keine Empfehlung." arrow>
                <Typography color="text.secondary" gutterBottom sx={{ cursor: 'help', fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                  Zuerst genannte Firma
                </Typography>
              </MuiTooltip>
              {statistics.champions && statistics.champions.length > 0 ? (
                <Box>
                  {statistics.champions.slice(0, 3).map((champion, index) => (
                    <Chip
                      key={index}
                      label={`${champion.name} (${champion.percentage.toFixed(1)}%)`}
                      size={isMobile ? 'small' : 'medium'}
                      sx={{
                        mr: 0.5,
                        mb: 0.5,
                        fontWeight: index === 0 ? 700 : 600,
                        ...(index === 0 && { backgroundColor: 'primary.light', color: 'primary.contrastText' }),
                      }}
                    />
                  ))}
                </Box>
              ) : (
                <Typography sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' }, fontWeight: 700 }}>-</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Prompts Used Section */}
      {promptBreakdown && promptBreakdown.prompts && promptBreakdown.prompts.length > 0 && project.jobs && project.jobs.length > 0 && (
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Verwendete Prompts
                </Typography>
                <Box>
                  {promptBreakdown.prompts.map((prompt, index) => {
                    // Get the first job's prompt values for this prompt
                    const firstJob = project.jobs[0];
                    const jobPrompt = firstJob.job_prompts?.find(jp => jp.prompt_id === prompt.id);
                    let displayContent = prompt.content;

                    // Replace placeholders with actual values
                    if (jobPrompt && jobPrompt.prompt_values) {
                      Object.entries(jobPrompt.prompt_values).forEach(([key, value]) => {
                        displayContent = displayContent.replace(new RegExp(`\\[${key}\\]`, 'g'), value);
                      });
                    }

                    return (
                      <Box
                        key={prompt.id}
                        onClick={() => {
                          setSelectedPromptId(prompt.id);
                          setSelectedPromptIndex(index);
                          setPromptComparisonOpen(true);
                        }}
                        sx={{
                          mb: 1.5,
                          pb: 1.5,
                          borderBottom: index < promptBreakdown.prompts.length - 1 ? '1px solid' : 'none',
                          borderColor: 'divider',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'action.hover',
                            borderRadius: 1,
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2">
                            {index + 1}. {displayContent}
                          </Typography>
                          {analyzePromptQuality(prompt.content) && (
                            <MuiTooltip title="Dieser Prompt ist eventuell zu kurz" arrow>
                              <WarningAmberIcon sx={{ color: 'warning.main', fontSize: '1.1rem', flexShrink: 0 }} />
                            </MuiTooltip>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Charts Section - Side by Side */}
      <Grid container spacing={3} mb={3}>
        {/* Trend Chart */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <MuiTooltip title="Zeigt den LLM Score je Job im Zeitverlauf: je Anbieter gemittelt, dann über die Anbieter. Läufe ohne gültiges Ergebnis haben keinen Punkt. Steigende Werte deuten auf verbesserte AI-Sichtbarkeit hin." arrow>
                  <Typography variant="h6" sx={{ cursor: 'help' }}>
                    Trend Over Time
                  </Typography>
                </MuiTooltip>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={show7TAvg}
                      onChange={(e) => setShow7TAvg(e.target.checked)}
                      size="small"
                    />
                  }
                  label="7T Mittelwert"
                  sx={{ mr: 0 }}
                />
              </Box>
              <ResponsiveContainer width="100%" height={isMobile ? 200 : 300}>
                <LineChart data={show7TAvg ? trendData7TAvg : trendData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: isMobile ? 10 : 12 }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: isMobile ? 10 : 12 }} />
                  <Tooltip formatter={show7TAvg ? formatSevenRunValue : undefined} />
                  {!isMobile && <Legend />}
                  <Line
                    type="monotone"
                    dataKey="score"
                    stroke="#8884d8"
                    name={show7TAvg ? "7T Mittelwert" : "Durchschnittliche Punktzahl"}
                    isAnimationActive={!reduceMotion}
                    animationDuration={800}
                    animationEasing="ease-out"
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Prompt Breakdown Chart */}
        {promptBreakdown && promptBreakdown.prompts && (
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <MuiTooltip title="Vergleicht die Scores der einzelnen Prompts pro Job. So erkennen Sie, welche Suchbegriffe besonders gut oder schlecht abschneiden." arrow>
                    <Typography variant="h6" sx={{ cursor: 'help' }}>
                      Per-Prompt Breakdown
                    </Typography>
                  </MuiTooltip>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={show7TAvgBreakdown}
                        onChange={(e) => setShow7TAvgBreakdown(e.target.checked)}
                        size="small"
                      />
                    }
                    label="7T Mittelwert"
                    sx={{ mr: 0 }}
                  />
                </Box>
                <ResponsiveContainer width="100%" height={isMobile ? 200 : 300}>
                  <BarChart data={show7TAvgBreakdown ? breakdownData7TAvg : breakdownData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: isMobile ? 10 : 12 }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: isMobile ? 10 : 12 }} />
                    <Tooltip formatter={show7TAvgBreakdown ? formatSevenRunValue : undefined} />
                    {!isMobile && (
                      <Legend
                        onClick={handleLegendClick}
                        wrapperStyle={{ cursor: 'pointer' }}
                      />
                    )}
                    {promptBreakdown.prompts.map((prompt, index) => (
                      <Bar
                        key={prompt.id}
                        dataKey={`prompt_${prompt.id}`}
                        fill={getPromptColors()[index % getPromptColors().length]}
                        name={`Prompt ${index + 1}`}
                        isAnimationActive={!reduceMotion}
                        animationDuration={800}
                        animationEasing="ease-out"
                        animationBegin={index * 100}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Aktuelle Benchmark-Modelle (zentral festgelegt) */}
      {activeTab === 0 && benchmarkModels.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 0.5,
            mb: 2,
            flexDirection: isMobile ? 'column' : 'row',
            ...(isMobile && { alignItems: 'flex-start' }),
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
            Aktuelle Benchmark-Modelle
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {benchmarkModels.map((m) => (
              <Chip key={m.model_code} label={m.display_name} size="small" variant="outlined" />
            ))}
          </Box>
        </Box>
      )}

      {/* Zitat-Anteil (nur mit gebuchtem Modul citation_share) */}
      {activeTab === 0 && (
        <CitationSharePanel
          projectId={projectId}
          canEdit={canEdit}
          tenantId={project?.tenant_id}
          models={benchmarkModels}
          prompts={(promptBreakdown?.prompts ?? []).map((pr) => ({
            id: pr.id,
            label: pr.content.length > 60 ? `${pr.content.slice(0, 60)}…` : pr.content,
          }))}
        />
      )}

      {/* Heatmap Chart - Score visualization over time */}
      {activeTab === 0 && (
        <Box mb={3}>
          {isMobile ? (
            <Accordion defaultExpanded={false}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Heatmap</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <HeatmapChart projectId={projectId} onCellClick={handleHeatmapCellClick} />
              </AccordionDetails>
            </Accordion>
          ) : (
            <HeatmapChart projectId={projectId} onCellClick={handleHeatmapCellClick} />
          )}
        </Box>
      )}

      {/* Jobs Table / Mobile Cards */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Test History
          </Typography>
          {isMobile ? (
            /* Mobile: Card layout */
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {project.jobs && project.jobs.length > 0 ? (
                [...project.jobs]
                  .sort((a, b) => compareJobsByCreation(b, a))
                  .map((job, index) => {
                    const wsProgress = jobProgress[job.id];
                    const apiProgress = (job.progress_current != null && job.progress_total != null && job.progress_total > 0)
                      ? { current: job.progress_current, total: job.progress_total, message: job.progress_message || '' }
                      : null;
                    const displayProgress = (wsProgress && apiProgress)
                      ? (wsProgress.current >= apiProgress.current ? wsProgress : apiProgress)
                      : (wsProgress || apiProgress);
                    const isRunning = job.status === 'running' || job.status === 'pending';
                    const previousJob = project.jobs[index + 1];

                    return (
                      <Card key={job.id} id={`job-${job.id}`} variant="outlined">
                        <CardActionArea onClick={() => handleViewJobDetails(job.id)} sx={{ p: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="subtitle2">#{project.jobs.length - index}</Typography>
                              <Chip
                                label={translateStatus(job.status)}
                                color={getStatusColor(job.status) as any}
                                size="small"
                              />
                              {isRunning && <LiveDot />}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" fontWeight={600}>
                                {formatJobScore(job)}
                              </Typography>
                              {getTrendIcon(job.average_score, previousJob?.average_score)}
                              <ChevronRightIcon sx={{ color: 'grey.400' }} />
                            </Box>
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(job.created_at, true)}
                          </Typography>
                          {isRunning && displayProgress && (
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="caption" display="block">{displayProgress.message}</Typography>
                              {displayProgress.current !== undefined && displayProgress.total !== undefined && displayProgress.total > 0 && (
                                <LinearProgress variant="determinate" value={(displayProgress.current / displayProgress.total) * 100} sx={{ mt: 0.5, '& .MuiLinearProgress-bar': { background: (displayProgress.current / displayProgress.total) >= 1 ? 'linear-gradient(90deg, #4caf50, #81c784)' : 'linear-gradient(90deg, #678BA3, #8AA4B8)' } }} />
                              )}
                            </Box>
                          )}
                        </CardActionArea>
                      </Card>
                    );
                  })
              ) : (
                <Typography color="text.secondary" textAlign="center">Keine Tests gefunden</Typography>
              )}
            </Box>
          ) : (
            /* Desktop: Table layout */
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Datum</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Punktzahl</TableCell>
                    <TableCell>Trend</TableCell>
                    <TableCell>Aktionen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {project.jobs && project.jobs.length > 0 ? (
                    [...project.jobs]
                      .sort((a, b) => compareJobsByCreation(b, a))
                      .map((job, index) => {
                        const wsProgress = jobProgress[job.id];
                        const apiProgress = (job.progress_current != null && job.progress_total != null && job.progress_total > 0)
                          ? { current: job.progress_current, total: job.progress_total, message: job.progress_message || '' }
                          : null;
                        const displayProgress = (wsProgress && apiProgress)
                          ? (wsProgress.current >= apiProgress.current ? wsProgress : apiProgress)
                          : (wsProgress || apiProgress);
                        const isRunning = job.status === 'running' || job.status === 'pending';
                        const previousJob = project.jobs[index + 1];

                        return (
                          <TableRow key={job.id} id={`job-${job.id}`}>
                            <TableCell>#{project.jobs.length - index}</TableCell>
                            <TableCell>{formatDate(job.created_at)}</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip
                                  label={translateStatus(job.status)}
                                  color={getStatusColor(job.status) as any}
                                  size="small"
                                />
                                {isRunning && <LiveDot />}
                              </Box>
                              {isRunning && displayProgress && (
                                <Box sx={{ mt: 1 }}>
                                  <Typography variant="caption" display="block">
                                    {displayProgress.message}
                                  </Typography>
                                  {displayProgress.current !== undefined && displayProgress.total !== undefined && displayProgress.total > 0 && (
                                    <LinearProgress
                                      variant="determinate"
                                      value={(displayProgress.current / displayProgress.total) * 100}
                                      sx={{ mt: 0.5, '& .MuiLinearProgress-bar': { background: (displayProgress.current / displayProgress.total) >= 1 ? 'linear-gradient(90deg, #4caf50, #81c784)' : 'linear-gradient(90deg, #678BA3, #8AA4B8)' } }}
                                    />
                                  )}
                                </Box>
                              )}
                            </TableCell>
                            <TableCell>
                              {formatJobScore(job)}
                            </TableCell>
                            <TableCell>
                              <MuiTooltip title={previousJob ? `Vorheriger Wert: ${previousJob.average_score != null ? previousJob.average_score.toFixed(2) : 'keiner'}` : 'Kein vorheriger Test'}>
                                <Box>
                                  {getTrendIcon(job.average_score, previousJob?.average_score)}
                                </Box>
                              </MuiTooltip>
                            </TableCell>
                            <TableCell>
                              <IconButton
                                size="small"
                                onClick={() => handleViewJobDetails(job.id)}
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        Keine Tests gefunden
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

        </Box></Fade>
      )}

      {/* Tab Content: Benchmark Tab (V3.0) */}
      {tabIndexMap['benchmark'] !== undefined && activeTab === tabIndexMap['benchmark'] && (
        <Fade in timeout={reduceMotion ? 0 : theme.motion.duration.base}><Box><OptimizationTab
          ref={optimizationTabRef}
          projectId={projectId}
          homepageUrl={project?.homepage_url}
          onConfigureHomepage={handleConfigureHomepage}
        /></Box></Fade>
      )}

      {/* Tab Content: Autopilot Tab (V4.1.0) */}
      {activeTab === tabIndexMap['autopilot'] && (
        <Fade in timeout={reduceMotion ? 0 : theme.motion.duration.base}><Box><AutopilotManagementCard
          projectId={projectId}
          projectName={project.name}
          tenantId={project.tenant_id}
          createDialogOpen={autopilotDialogOpen}
          onOpenCreateDialog={() => setAutopilotDialogOpen(true)}
          onCloseCreateDialog={() => setAutopilotDialogOpen(false)}
        /></Box></Fade>
      )}

      {/* Job Detail Dialog */}
      <JobDetailDialog />

      {/* Prompt Comparison Dialog */}
      {promptBreakdown && (
        <PromptComparisonDialog
          open={promptComparisonOpen}
          onClose={() => setPromptComparisonOpen(false)}
          promptId={selectedPromptId}
          promptIndex={selectedPromptIndex}
          jobIds={promptBreakdown.data.map(item => item.job_id)}
          testNames={promptBreakdown.data.map(item => item.test_name.replace('Test ', '#'))}
          searchTerm={project?.search_term || ''}
          searchTermSynonym1={project?.search_term_synonym1}
          searchTermSynonym2={project?.search_term_synonym2}
          breakdownData={promptBreakdown.data}
        />
      )}

      {/* Homepage URL Dialog (V3.0) */}
      <HomepageUrlDialog
        open={homepageDialogOpen}
        onClose={() => setHomepageDialogOpen(false)}
        projectId={projectId}
        currentUrl={project?.homepage_url}
        onSuccess={handleHomepageUrlSuccess}
      />

      {/* Toast Notification */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={3000}
        onClose={handleToastClose}
        message={toastMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          '& .MuiSnackbarContent-root': {
            backgroundColor: '#323232',
            color: '#fff',
            fontSize: '1rem',
            fontWeight: 500,
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          },
        }}
      />
    </Box>
  );
};

export default ProjectDetail;
